import styled from 'styled-components';
import { Circle } from 'ui/icons';
import { BasicOption } from 'ui/selects';
import { colors, spacing, body2, heading4, fontSizes, fontWeights, subtitle2 } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { FLOWCHART_Z_INDEXES } from '../WorkflowFlowchart/WorkflowFlowchart.styles';

/* ---------- SHARED STYLES ---------- */
export const StyledBody = styled.div`
  background: ${colors.white};
  padding-top: 0;
  display: flex;
  overflow-wrap: normal;
  height: 100%;
  width: 100%;
`;

export const Labels = styled.p`
  ${subtitle2};
`;

export const EnabledStatus = styled.p`
  ${heading4};
  color: ${colors.grey60};
  padding: ${spacing.eighth};
  box-sizing: border-box;
`;

export const RetryLink = styled.span`
  margin-left: ${spacing.eighth};
  cursor: pointer;
  text-decoration: underline;
`;

export const LoadingWrapper = styled.div`
  width: 100%;
  align-items: center;
  padding: ${spacing.half};
  color: ${colors.grey25};
`;

/* ---------- REVISION HISTORY TAKEOVER (LEFT) ---------- */
export const RevisionHistoryLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  width: ${pxToRem(350)};
  min-width: ${pxToRem(350)};
  height: 100%;
  padding: ${spacing.base};
  border-right: ${spacing.single} solid ${colors.grey15};
  gap: ${spacing.threeEights};
`;

const RevisionOptionHeight = 95;

export const RevisionOption = styled(BasicOption)<{ isSelected?: boolean }>`
  ${body2}
  width: 100%;
  height: ${pxToRem(RevisionOptionHeight)};
  padding: ${spacing.base};
  display: flex;
  position: relative;
  overflow: visible;

  ${({ isSelected }) =>
    isSelected &&
    `
      :not(:hover) {
        color: ${colors.textLink};
      }
    `}

  :not(:first-child) {
    :before {
      position: absolute;
      content: '';
      background-color: ${colors.grey15};
      width: ${pxToRem(1)};
      height: ${pxToRem(RevisionOptionHeight - 2)};
      bottom: ${pxToRem(62)};
      left: ${pxToRem(20)};
    }
  }
`;

export const StyledCircle = styled(Circle)`
  display: flex;
  position: relative;
  top: ${pxToRem(16)};
`;

export const RevisionOptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding-left: ${pxToRem(26)};
  left: ${pxToRem(4)};
  font-size: ${fontSizes.s15};
`;

export const RevisionUser = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.half};
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s13};
  font-weight: ${fontWeights.light};
  height: ${pxToRem(30)};
  align-items: center;
`;

export const RevisionUserName = styled.div`
  width: ${pxToRem(200)};
  /** NOTE: in case users have longer than avg names */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* ---------- REVISION HISTORY TAKEOVER (RIGHT) ---------- */
export const RevisionHistoryRightContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const StyledContent = styled.div`
  display: flex;
  background-color: ${colors.grey10};
  justify-content: flex-end;
  align-items: center;
  padding: ${spacing.base};
  height: fit-content;
  border: ${spacing.single} solid ${colors.grey15};
`;

export const RevisionDataContainer = styled.div`
  padding: ${spacing.base};
  display: flex;
  flex-direction: column;
  gap: ${spacing.plusHalf};
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

export const NoData = styled.div`
  ${body2};
  color: ${colors.grey25};
  padding: ${spacing.threeQuarter};
  box-sizing: border-box;
`;

/* ---------- REVISION PREVIEW ---------- */
export const PreviewContainer = styled.div`
  width: 100%;
  height: 70%;
  display: flex;
  flex-direction: column;
`;

export const SelectedRevisionPreview = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

export const PreviewWrapper = styled.div`
  z-index: ${FLOWCHART_Z_INDEXES.CANVAS};
  position: relative;
  border-radius: ${spacing.quarter};
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

/* ---------- REVISION CHANGELOG ---------- */
export const ChangelogContainer = styled.div`
  height: 30%;
  display: flex;
  flex-direction: column;
`;

export const SelectedRevisionChangelog = styled.div`
  border-radius: ${spacing.quarter};
  border: 1px solid ${colors.grey25};
  overflow-y: auto;
  height: ${pxToRem(130)};
`;

/* ---------- NO REVISIONS ---------- */

export const NoRevisionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: ${pxToRem(24)};
  background-color: ${colors.backgroundSecondary};
  padding: ${pxToRem(96)} ${pxToRem(24)};
  font-size: 16px;
  border-radius: ${pxToRem(4)};

  > :nth-child(2) {
    font-weight: 600;
  }

  > :last-child {
    color: ${colors.textSecondary};
    max-width: ${pxToRem(475)};
  }
`;
